import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import { FiZap, FiArrowLeft } from "react-icons/fi";
import ProductionLineForm from "./ProductionLineForm";
import { withAuth } from "../../../components/HOC/withAuth";
import "./ProductionLines.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const ProductionLineFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  useTitle(
    isEdit
      ? "Rovion MES - Üretim Hattı Düzenle"
      : "Rovion MES - Yeni Üretim Hattı",
  );

  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/production-lines",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  useEffect(() => {
    if (currentUser && !canWrite) {
      navigate("/production-lines");
    }
  }, [currentUser, canWrite, navigate]);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const [machines, setMachines] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    machineItems: [],
    productLineType: "production",
    dataEntryType: "manual",
    notes: "",
  });

  const addToast = useCallback((message, type = "info") => {
    const t = { id: Date.now(), message, type };
    setToasts((p) => [...p, t]);
    setTimeout(() => setToasts((p) => p.filter((x) => x.id !== t.id)), 3000);
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const { headers } = authHeaders();
        const [machinesRes, invRes] = await Promise.all([
          axios.get(`${API_URL}/api/machines`, { headers }),
          axios.get(`${API_URL}/api/inventory`, { headers }),
        ]);
        setMachines(machinesRes.data?.machines || machinesRes.data || []);
        setInventoryItems(invRes.data?.items || invRes.data || []);

        if (isEdit) {
          const lineRes = await axios.get(
            `${API_URL}/api/production-lines/${id}`,
            { headers },
          );
          const line = lineRes.data?.line;
          if (line) {
            setFormData({
              name: line.name,
              code: line.code,
              machineItems: (line.machineItems || []).map((mi) => ({
                machineId: mi.machineId?._id || mi.machineId,
                lineItems: (mi.lineItems || []).map((li) => ({
                  inventoryItemId:
                    li.inventoryItemId?._id || li.inventoryItemId,
                  quantityUsed: li.quantityUsed,
                })),
                entryItemId: mi.entryItemId?._id || mi.entryItemId || undefined,
                outputItemId:
                  mi.outputItemId?._id || mi.outputItemId || undefined,
                inputAmount:
                  mi.inputAmount != null
                    ? mi.inputAmount
                    : mi.inputPerOutput != null
                      ? mi.inputPerOutput
                      : 1,
                outputAmount: mi.outputAmount != null ? mi.outputAmount : 1,
                inputPerOutput:
                  mi.inputPerOutput != null
                    ? mi.inputPerOutput
                    : (() => {
                        const inAmt =
                          mi.inputAmount != null
                            ? mi.inputAmount
                            : mi.inputPerOutput != null
                              ? mi.inputPerOutput
                              : 1;
                        const outAmt =
                          mi.outputAmount != null ? mi.outputAmount : 1;
                        return outAmt > 0 ? inAmt / outAmt : 1;
                      })(),
              })),
              productLineType: line.productLineType || "production",
              dataEntryType: line.dataEntryType || "manual",
              notes: line.notes || "",
            });
          } else {
            addToast("Hattı bulunamadı", "danger");
            navigate("/production-lines");
          }
        }
      } catch (err) {
        addToast(isEdit ? "Veri yüklenemedi" : "Veriler yüklenemedi", "danger");
        if (isEdit) navigate("/production-lines");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, isEdit, navigate, authHeaders, addToast]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      addToast("Ad ve kod zorunludur", "warning");
      return;
    }
    if (!formData.machineItems?.length) {
      addToast("En az bir makine ekleyin", "warning");
      return;
    }
    const payload = {
      ...formData,
      machineItems: formData.machineItems.map((mi) => {
        const inputAmount =
          mi.inputAmount != null
            ? Number(mi.inputAmount) || 1
            : mi.inputPerOutput != null
              ? Number(mi.inputPerOutput) || 1
              : 1;
        const outputAmount =
          mi.outputAmount != null ? Number(mi.outputAmount) || 1 : 1;
        const inputPerOutput =
          outputAmount > 0 ? inputAmount / outputAmount : inputAmount;
        return {
          machineId: mi.machineId?._id || mi.machineId,
          lineItems: (mi.lineItems || []).map((li) => ({
            inventoryItemId: li.inventoryItemId?._id || li.inventoryItemId,
            quantityUsed: Number(li.quantityUsed) || 1,
          })),
          entryItemId: mi.entryItemId || undefined,
          outputItemId: mi.outputItemId || undefined,
          inputAmount,
          outputAmount,
          inputPerOutput,
        };
      }),
    };
    try {
      const { headers } = authHeaders();
      if (isEdit) {
        await axios.put(
          `${API_URL}/api/production-lines/${id}`,
          payload,
          { headers },
        );
        addToast("Üretim hattı güncellendi", "success");
      } else {
        await axios.post(`${API_URL}/api/production-lines`, payload, {
          headers,
        });
        addToast("Üretim hattı oluşturuldu", "success");
      }
      navigate("/production-lines");
    } catch (err) {
      addToast(err.response?.data?.message || "Kaydedilemedi", "danger");
    }
  };

  const handleCancel = () => navigate("/production-lines");

  if (loading) {
    return (
      <AppPage
        title={isEdit ? "Hattı Düzenle" : "Yeni Üretim Hattı"}
        icon={<FiZap />}
      >
        <CardContainer>
          <div className="loading-container">
            <FiZap className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        </CardContainer>
      </AppPage>
    );
  }

  return (
    <AppPage
      title={isEdit ? "Hattı Düzenle" : "Yeni Üretim Hattı"}
      subtitle={
        isEdit
          ? "Üretim hattı bilgilerini güncelleyin"
          : "Yeni üretim hattı oluşturun"
      }
      icon={<FiZap />}
      actions={
        <Button
          appearance="subtle"
          onClick={handleCancel}
          icon={<FiArrowLeft />}
        >
          Geri
        </Button>
      }
    >
      <CardContainer>
        <ProductionLineForm
          formData={formData}
          setFormData={setFormData}
          machines={machines}
          inventoryItems={inventoryItems}
          editingLine={isEdit ? { _id: id } : null}
          addToast={addToast}
          onSubmit={handleSubmit}
          onReset={handleCancel}
        />
      </CardContainer>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type}
            onClose={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(ProductionLineFormPage, ["admin", "employee", "user"]);
